import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";
import { LayoutGrid, LayoutList, Search } from "lucide-react";

export type RagDocumentsViewMode = "table" | "cards";

interface RagDocumentsListToolbarProps {
  searchQuery: string;
  onSearchChange: (value: string) => void;
  view: RagDocumentsViewMode;
  onViewChange: (view: RagDocumentsViewMode) => void;
  placeholder?: string;
  ariaLabel?: string;
}

export function RagDocumentsListToolbar({
  searchQuery,
  onSearchChange,
  view,
  onViewChange,
  placeholder = "Search documents…",
  ariaLabel = "Search documents",
}: RagDocumentsListToolbarProps) {
  return (
    <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
      <div className="relative w-full min-w-0 max-w-[240px] sm:max-w-[280px]">
        <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-500" />
        <Input
          value={searchQuery}
          onChange={(event) => onSearchChange(event.target.value)}
          placeholder={placeholder}
          className="pl-9"
          aria-label={ariaLabel}
        />
      </div>
      <div
        className="flex shrink-0 rounded-lg border border-[rgba(15,23,42,0.09)] p-0.5 dark:border-white/10"
        role="group"
        aria-label="Document list view"
      >
        <button
          type="button"
          title="Table view"
          aria-label="Table view"
          aria-pressed={view === "table"}
          onClick={() => onViewChange("table")}
          className={cn(
            "inline-flex items-center gap-1 rounded-md px-2.5 py-1.5 text-xs font-medium transition-colors",
            view === "table"
              ? "bg-primary text-primary-foreground"
              : "text-[#576274] hover:bg-[var(--lm-hover)] dark:text-gray-400 dark:hover:bg-white/10"
          )}
        >
          <LayoutList className="h-3.5 w-3.5" />
          <span className="hidden sm:inline">Table</span>
        </button>
        <button
          type="button"
          title="Tile view"
          aria-label="Tile view"
          aria-pressed={view === "cards"}
          onClick={() => onViewChange("cards")}
          className={cn(
            "inline-flex items-center gap-1 rounded-md px-2.5 py-1.5 text-xs font-medium transition-colors",
            view === "cards"
              ? "bg-primary text-primary-foreground"
              : "text-[#576274] hover:bg-[var(--lm-hover)] dark:text-gray-400 dark:hover:bg-white/10"
          )}
        >
          <LayoutGrid className="h-3.5 w-3.5" />
          <span className="hidden sm:inline">Tiles</span>
        </button>
      </div>
    </div>
  );
}
